import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Download, ShieldCheck, Clock, FileStack, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/cpa-review`;
const fmt = (iso) => new Date(iso).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });

const SECTION_LABELS = { tracker: 'Savings tracker', report: 'Strategy Advisor report', chats: 'Advisor conversations' };

export default function CpaReview() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const fetched = useRef(false);

  useEffect(() => {
    if (fetched.current) return;  // count one view per page load (StrictMode double-invokes effects)
    fetched.current = true;
    axios.get(`${API}/${token}`)
      .then(r => setInfo(r.data))
      .catch(e => setError(e.response?.data?.detail || 'This review link could not be opened.'));
  }, [token]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-stone-50 px-4" data-testid="cpa-review-error">
        <div className="max-w-md text-center">
          <AlertTriangle className="mx-auto h-10 w-10 text-amber-500" />
          <h1 className="mt-4 text-2xl font-bold text-stone-900">Link unavailable</h1>
          <p className="mt-2 text-stone-600">{error}. Ask the person who shared it to send a new one.</p>
        </div>
      </div>
    );
  }
  if (!info) return <div className="min-h-screen bg-stone-50" data-testid="cpa-review-loading" />;

  const included = ['Summary', ...info.sections.filter(s => s !== 'report' || info.has_report).map(s => SECTION_LABELS[s])];

  return (
    <div className="min-h-screen bg-stone-100" data-testid="cpa-review-page">
      <header className="border-b border-stone-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-4 px-4 py-5 sm:px-6">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-600 text-white"><FileStack className="h-5 w-5" /></span>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700">SolelyTax · Read-only CPA review</p>
            <h1 className="text-xl font-bold text-stone-900" data-testid="cpa-review-title">{info.shared_by}'s {info.year} year-end packet</h1>
          </div>
          <Button asChild className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="cpa-review-download-btn">
            <a href={`${API}/${token}/pdf?download=true`}><Download className="mr-2 h-4 w-4" /> Download PDF</a>
          </Button>
        </div>
        <div className="mx-auto flex max-w-6xl flex-wrap gap-x-6 gap-y-1 px-4 pb-4 text-xs text-stone-500 sm:px-6">
          <span className="flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-emerald-600" /> Private link · don't forward</span>
          <span className="flex items-center gap-1.5" data-testid="cpa-review-expiry"><Clock className="h-3.5 w-3.5" /> Shared {fmt(info.shared_at)} · expires {fmt(info.expires_at)}</span>
          <span data-testid="cpa-review-sections">Includes: {included.join(' · ')}{info.advisor_names.length ? ` (${info.advisor_names.join(', ')})` : ''}</span>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
        <iframe title="CPA packet" src={`${API}/${token}/pdf`} className="h-[80vh] w-full rounded-xl border border-stone-200 bg-white shadow-sm" data-testid="cpa-review-pdf-frame" />
        <p className="mt-3 text-center text-xs text-stone-500">Prepared from the taxpayer's own records and AI-generated guidance. Not tax advice.</p>
      </main>
    </div>
  );
}
